import React, { createContext, Dispatch, SetStateAction, useState, useContext, useCallback, useMemo } from 'react';
import { ConfirmDialog } from '../components/ConfirmDialog';

interface IProps {
  children: JSX.Element;
}

export interface IDialogConfig {
  title?: string;
  text?: string;
  open?: boolean;
  pending?: boolean;
  proceedButtonText?: string;
  cancelButtonText?: string;
  onProceed: () => void;
  onCancel?: () => void;
}

const InitialTitle = 'Are you sure?';
const InitialText = 'It will be applied to the app immediately.';
const initialState: IDialogConfig = {
  title: InitialTitle,
  text: InitialText,
  open: false,
  pending: false,
  onProceed: () => undefined,
  onCancel: () => undefined,
};

export const ConfirmDialogContextComponent = (props: IProps): JSX.Element => {
  const [dialogConfig, setDialogConfig] = useState<IDialogConfig>(initialState);

  const { title, text, open, pending, onProceed, onCancel, proceedButtonText, cancelButtonText } = dialogConfig;

  const close = () => {
    const cloned = { ...dialogConfig };
    cloned.open = false;
    setDialogConfig(cloned);
  };

  return (
    <ConfirmDialogContext.Provider value={setDialogConfig}>
      {props.children}
      <ConfirmDialog
        open={!!open}
        isPending={pending}
        title={title || InitialTitle}
        text={text || InitialText}
        onPressProceed={() => {
          close();
          onProceed();
        }}
        onPressCancel={() => {
          close();
          if (onCancel) {
            onCancel();
          }
        }}
        proceedButtonText={proceedButtonText}
        cancelButtonText={cancelButtonText}
      />
    </ConfirmDialogContext.Provider>
  );
};

const ConfirmDialogContext = createContext<Dispatch<SetStateAction<IDialogConfig>>>(() => undefined);

const useConfirmDialog = () => {
  const confirmDialogContext = useContext(ConfirmDialogContext);

  const open = useCallback(
    (config: IDialogConfig) => {
      confirmDialogContext({
        open: true,
        ...config,
      });
    },
    [confirmDialogContext],
  );

  const close = useCallback(() => {
    confirmDialogContext({
      open: false,
      onProceed: () => undefined,
    });
  }, [confirmDialogContext]);

  return useMemo(() => ({ open, close }), [open, close]);
};

export default useConfirmDialog;
